import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Link, Navigate } from 'react-router-dom';
import {
  Upload, Download, CheckCircle2, XCircle, Loader2, Package, Zap,
  FileText, Calendar, Hash, HardDrive, ArrowLeft, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const PLATFORMS = [
  { key: 'macos',   label: 'macOS',   ext: '.dmg',      accept: '.dmg' },
  { key: 'windows', label: 'Windows', ext: '.exe',      accept: '.exe' },
  { key: 'ubuntu',  label: 'Ubuntu',  ext: '.AppImage', accept: '.AppImage,.appimage' },
];

function humanSize(bytes) {
  if (!bytes) return '—';
  const mb = bytes / (1024 * 1024);
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}

async function sha256(file) {
  const buf = await file.arrayBuffer();
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/* ═══════════════════════════ Page ══════════════════════════════════ */
export default function AdminDesktopPage() {
  useDocumentTitle('Admin · Desktop Releases · CRATELAB');
  const { user, isAuthenticated } = useAuth() || {};
  const [releases, setReleases] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/api/desktop/versions`);
        setReleases(data.platforms || {});
      } catch { setReleases({}); }
    })();
  }, [isAuthenticated, refreshTick]);

  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user && user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-md p-8 rounded-2xl border border-red-200 bg-red-50 text-center">
          <XCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <h1 className="font-heading text-xl font-bold text-red-800 mb-2">Admin only</h1>
          <p className="font-body text-sm text-red-700">
            You need an admin role to publish desktop installers. Ask an existing admin to grant you access.
          </p>
          <Link to="/" className="inline-block mt-4 font-mono text-xs uppercase tracking-wider text-red-700 hover:underline">
            ← Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-4 sm:p-8" data-testid="admin-desktop-page">
      <div className="max-w-4xl mx-auto">
        <Link to="/dashboard" className="inline-flex items-center gap-2 mb-6 font-mono text-xs uppercase tracking-wider text-[#64748B] hover:text-[#0F172A]">
          <ArrowLeft className="w-4 h-4" /> Back to dashboard
        </Link>

        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#2563FF]/10 border border-[#2563FF]/20 rounded-full mb-3">
            <Sparkles className="w-3.5 h-3.5 text-[#2563FF]" />
            <span className="font-mono text-[11px] uppercase tracking-wider text-[#2563FF]">Admin</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight mb-2">
            Desktop releases
          </h1>
          <p className="font-body text-[#64748B]">
            Upload signed installers to R2 and publish them. The public analyzer page picks up the new
            build automatically once you hit publish.
          </p>
        </div>

        {/* Current releases table */}
        <div className="mb-10 bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-[#E2E8F0]">
            <h2 className="font-heading text-lg font-bold text-[#0F172A]">Current published releases</h2>
          </div>
          <div className="divide-y divide-[#E2E8F0]">
            {PLATFORMS.map((p) => {
              const r = releases?.[p.key];
              const avail = !!r?.available;
              return (
                <div key={p.key} className="flex items-center gap-4 px-6 py-4" data-testid={`admin-release-row-${p.key}`}>
                  <Package className={`w-5 h-5 ${avail ? 'text-[#2563FF]' : 'text-[#94A3B8]'}`} />
                  <div className="flex-1 min-w-0">
                    <p className="font-heading text-base font-semibold text-[#0F172A]">{p.label}</p>
                    <p className="font-mono text-xs text-[#64748B]">
                      {avail
                        ? `v${r.version} · ${humanSize(r.size_bytes)} · released ${new Date(r.released_at).toLocaleDateString()}`
                        : 'Not yet published'}
                    </p>
                  </div>
                  {avail
                    ? <span className="px-2 py-1 bg-green-100 text-green-700 border border-green-200 font-mono text-[10px] uppercase tracking-wider rounded">Live</span>
                    : <span className="px-2 py-1 bg-slate-100 text-slate-500 border border-slate-200 font-mono text-[10px] uppercase tracking-wider rounded">—</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Upload cards */}
        <h2 className="font-heading text-lg font-bold text-[#0F172A] mb-4">Upload a new build</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {PLATFORMS.map((p) => (
            <UploadCard
              key={p.key}
              platform={p}
              onPublished={() => {
                toast.success(`${p.label} release published`);
                setRefreshTick((t) => t + 1);
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════ Upload card ═══════════════════════════ */
function UploadCard({ platform, onPublished }) {
  const [file, setFile]           = useState(null);
  const [version, setVersion]     = useState('');
  const [notes, setNotes]         = useState('');
  const [phase, setPhase]         = useState('idle'); // idle | hashing | initing | uploading | publishing | done | error
  const [progress, setProgress]   = useState(0);
  const [error, setError]         = useState(null);
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null); setVersion(''); setNotes('');
    setPhase('idle'); setProgress(0); setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleFile = useCallback((f) => {
    if (!f) return;
    const acceptable = platform.accept.split(',').some((ext) =>
      f.name.toLowerCase().endsWith(ext.trim().toLowerCase()),
    );
    if (!acceptable) {
      toast.error(`Expected ${platform.ext} for ${platform.label}`);
      return;
    }
    setFile(f); setError(null);
    // Try to auto-extract version from filename e.g. "CRATELAB-1.3.8.exe"
    const m = f.name.match(/(\d+\.\d+\.\d+)/);
    if (m) setVersion(m[1]);
  }, [platform]);

  const upload = async () => {
    if (!file || !version) {
      toast.error('Pick a file and set a version');
      return;
    }
    setError(null);

    try {
      // 1) Hash locally so we can store the SHA-256 for tamper-detection
      setPhase('hashing');
      const hash = await sha256(file);

      // 2) Ask backend for a presigned PUT URL
      setPhase('initing');
      const { data: init } = await axios.post(`${API_BASE}/api/admin/desktop/upload-init`, {
        platform:   platform.key,
        version,
        size_bytes: file.size,
      });

      // 3) PUT directly to R2 with progress.
      // NOTE: We deliberately do NOT set Content-Type here. R2 signature
      // validation is strict — if we send a Content-Type that differs from
      // what was signed, we get 403. Backend now signs WITHOUT ContentType
      // so any (or no) Content-Type header from the browser is accepted.
      setPhase('uploading');
      setProgress(0);
      await axios.put(init.upload_url, file, {
        onUploadProgress: (evt) => {
          const total = evt.total || file.size;
          setProgress(Math.round((evt.loaded * 100) / total));
        },
      });

      // 4) Publish — flip is_current
      setPhase('publishing');
      await axios.post(`${API_BASE}/api/admin/desktop/publish`, {
        platform:      platform.key,
        version,
        storage_key:   init.storage_key,
        filename:      init.filename,
        size_bytes:    file.size,
        sha256:        hash,
        release_notes: notes || null,
      });

      setPhase('done');
      onPublished?.();
      setTimeout(reset, 2500);
    } catch (e) {
      setPhase('error');
      setError(e?.response?.data?.detail || e.message || 'Upload failed');
    }
  };

  const busy = ['hashing', 'initing', 'uploading', 'publishing'].includes(phase);
  const phaseLabel = {
    hashing:    'Computing SHA-256…',
    initing:    'Requesting upload URL…',
    uploading:  `Uploading… ${progress}%`,
    publishing: 'Publishing release…',
    done:       'Published',
  }[phase];

  return (
    <div className="p-5 rounded-2xl border border-[#E2E8F0] bg-white" data-testid={`admin-upload-${platform.key}`}>
      <div className="flex items-center gap-2 mb-4">
        <Package className="w-5 h-5 text-[#2563FF]" />
        <h3 className="font-heading text-base font-bold text-[#0F172A]">{platform.label}</h3>
        <span className="ml-auto font-mono text-[10px] text-[#94A3B8]">{platform.ext}</span>
      </div>

      {/* File picker */}
      <label
        htmlFor={`file-${platform.key}`}
        className={`block p-4 mb-3 border-2 border-dashed rounded-xl cursor-pointer transition-colors ${
          file ? 'border-[#2563FF] bg-[#EFF6FF]' : 'border-[#CBD5E1] hover:border-[#2563FF]/50 hover:bg-[#F8FAFC]'
        }`}
      >
        <input
          ref={inputRef}
          id={`file-${platform.key}`}
          type="file"
          accept={platform.accept}
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
          data-testid={`admin-file-${platform.key}`}
        />
        {file ? (
          <div className="text-center">
            <CheckCircle2 className="w-6 h-6 text-[#2563FF] mx-auto mb-1" />
            <p className="font-mono text-xs text-[#0F172A] truncate">{file.name}</p>
            <p className="font-mono text-[10px] text-[#64748B]">{humanSize(file.size)}</p>
          </div>
        ) : (
          <div className="text-center">
            <Upload className="w-6 h-6 text-[#94A3B8] mx-auto mb-1" />
            <p className="font-mono text-xs text-[#64748B]">Click to select {platform.ext}</p>
          </div>
        )}
      </label>

      {/* Version */}
      <input
        type="text"
        placeholder="Version (e.g. 1.3.8)"
        value={version}
        onChange={(e) => setVersion(e.target.value.trim())}
        className="w-full px-3 py-2 mb-3 border border-[#E2E8F0] rounded-lg font-mono text-sm focus:outline-none focus:border-[#2563FF]"
        data-testid={`admin-version-${platform.key}`}
      />

      {/* Release notes */}
      <textarea
        placeholder="Release notes (optional)"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={2}
        className="w-full px-3 py-2 mb-3 border border-[#E2E8F0] rounded-lg font-body text-sm focus:outline-none focus:border-[#2563FF] resize-none"
        data-testid={`admin-notes-${platform.key}`}
      />

      {/* Action */}
      <button
        onClick={upload}
        disabled={busy || !file || !version}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#0F172A] hover:bg-[#2563FF] disabled:bg-[#94A3B8] disabled:cursor-not-allowed text-white rounded-lg font-heading text-xs uppercase tracking-wider transition-colors"
        data-testid={`admin-publish-${platform.key}`}
      >
        {busy
          ? <><Loader2 className="w-4 h-4 animate-spin" /> {phaseLabel}</>
          : phase === 'done'
            ? <><CheckCircle2 className="w-4 h-4" /> Published</>
            : <><Zap className="w-4 h-4" /> Upload &amp; Publish</>}
      </button>

      {/* Progress bar */}
      {phase === 'uploading' && (
        <div className="mt-3 h-1.5 bg-[#F1F5F9] rounded-full overflow-hidden">
          <div className="h-full bg-[#2563FF] transition-all" style={{ width: `${progress}%` }} />
        </div>
      )}

      {error && (
        <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-red-700 font-mono text-[10px]" data-testid={`admin-error-${platform.key}`}>
          {error}
        </div>
      )}
    </div>
  );
}
